import { LitElement, html, css, PropertyValues } from 'lit';
import { customElement, property,  } from 'lit/decorators.js';
import { VesselPlanningModel, VoyagePlan } from '../../../src/models/vessel-planning-model';
import { ChartNames, echartUtils, PilotPointTimestamps, TimeseriesData, TimeseriesValues, TimestampData, WaypointTimestamps } from '../../voy-timeseries-chart/src/echartUtils';
import { extractAllTimestamps, getHardWaypointsInfoFromRoutes, getPilotPointIndeces, getPilotPointsTimestamps } from '../../../src/utils/route-utils';
import '../../voy-timeseries-chart/src/voy-timeseries-chart'
import { ParameterType, VesselPerformanceModel } from '../../../src/models/vessel-performance-model';
import { PlanDataPoint } from '../../../src/models/vessel-performance-model-impl';
import { PerformanceUtils } from '../../../src/utils/performance-utils';
import { VoyTimeseriesChart } from '../../voy-timeseries-chart/src/voy-timeseries-chart'
import { debounce } from '../../../src/utils/helpers';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import { EtaChangeChartStyles } from './eta-change-review-styles';

@customElement('eta-change-chart')
export class EtaChangeChart extends LitElement {
  @property({ type: Object, attribute: false }) requestedPlan: VoyagePlan | null = null;
  @property({ attribute: false }) private pilotPoints: PilotPointTimestamps = { departure: -1, arrival: -1 };
  @property({ attribute: false }) private newPilotPoints: PilotPointTimestamps = { departure: -1, arrival: -1 };
  @property({ attribute: false }) private hardWaypoints: WaypointTimestamps[][] = [];
  @property({ attribute: false }) private allTimestamps: TimestampData[] = [];
  @property({ attribute: false }) private currentPlanDataPoints: PlanDataPoint[] = [];
  @property({ attribute: false }) private requestedPlanDataPoints: PlanDataPoint[] = [];
  @property({ attribute: false }) private _sogData: TimeseriesData[] = [];
  @property({ attribute: false }) private _powerData: TimeseriesData[] = [];
  @property({ attribute: false }) private _stwData: TimeseriesData[] = [];
  @property({ attribute: false }) private _currentImpactData: TimeseriesData[] = [];
  @property({ attribute: false }) private _rollAngleData: TimeseriesData[] = [];
  @property({ attribute: false }) private isNewRouteEtaAfterCurrent: boolean = false;
  private _debouncedUpdateData: () => void;
  private _debouncedExtractAllTimestamps: () => void;
  private _rollAngleThresholdLimit: number | null = null; // Set this to a number to display the threshold limit in the chart
  @property({ attribute: false }) private _chartsData: { [index in ChartNames]: TimeseriesData[] } = {
    sog: [],
    power: [],
    rpm: [],
    wind50M: [],
    currents: [],
    wave: [],
    swell: [],
    stw: [],
    currentImpact: [],
    rollAngle: []
  };
  static styles = EtaChangeChartStyles;

  constructor() {
    super();
    this.updateData = this.updateData.bind(this);
    this._debouncedUpdateData = debounce(this.updateData, 200);
    this.updatePerformanceData = this.updatePerformanceData.bind(this);
    this.updateActualPerformanceData = this.updateActualPerformanceData.bind(this);
    this.extractAllTimestamps = this.extractAllTimestamps.bind(this);
    this._debouncedExtractAllTimestamps = debounce(this.extractAllTimestamps, 200)

    this.updateData();
    this.updateActualPerformanceData();
    this.extractAllTimestamps();
  }

  firstUpdated() {

    VesselPerformanceModel!.addEventListener('performance-updated', this.updatePerformanceData);
    VesselPerformanceModel!.addEventListener('papi-performance-updated', this.updateActualPerformanceData);
    VesselPlanningModel!.addEventListener('executing-plan-changed', this._debouncedUpdateData);

    
    const sogchart = <VoyTimeseriesChart>this.shadowRoot!.querySelector('#sogchart');
    const powerchart = <VoyTimeseriesChart>this.shadowRoot!.querySelector('#powerchart');
    const stwchart = <VoyTimeseriesChart>this.shadowRoot!.querySelector('#stwchart');
    const currentImpactChart = <VoyTimeseriesChart>this.shadowRoot!.querySelector('#currentImpactchart');
    const rollAngleChart = <VoyTimeseriesChart>this.shadowRoot!.querySelector('#rollAnglechart');
    VoyTimeseriesChart.connect([sogchart, powerchart, stwchart, currentImpactChart, rollAngleChart]);
  }

  updated(changedProps: PropertyValues) {
    if (changedProps.has('requestedPlan')) {
      this.updateData();
    }
  }
  
  connectedCallback() {
    super.connectedCallback();
    VesselPerformanceModel!.addEventListener('performance-updated', this.updatePerformanceData);
    VesselPerformanceModel!.addEventListener('papi-performance-updated', this.updateActualPerformanceData);
    VesselPlanningModel!.addEventListener('executing-plan-changed', this._debouncedUpdateData);

  }
  
  disconnectedCallback()
  {
      super.disconnectedCallback();
      VesselPerformanceModel!.removeEventListener('performance-updated', this.updatePerformanceData);
      VesselPerformanceModel!.removeEventListener('papi-performance-updated', this.updateActualPerformanceData);
      VesselPlanningModel!.removeEventListener('executing-plan-changed', this._debouncedUpdateData)
  }

  private async updateData() {
    const currentPlan = await VesselPlanningModel!.getExecutingPlan();

    if (!currentPlan) return;

    this._rollAngleThresholdLimit = currentPlan.rollAngle || null;

    this.pilotPoints = getPilotPointsTimestamps(currentPlan);
    this.newPilotPoints = getPilotPointsTimestamps(this.requestedPlan ?? null)

    this.isNewRouteEtaAfterCurrent = this.newPilotPoints.arrival > this.pilotPoints.arrival 

    const currentApprovedRoute = currentPlan.routes[currentPlan.selectedRouteIndex];
    const newRequestedRoute = this.requestedPlan?.routes[this.requestedPlan.selectedRouteIndex];
    if (!currentApprovedRoute) return;

    const currentRouteWaypoints = getHardWaypointsInfoFromRoutes([currentApprovedRoute]);
    const newRouteWaypoints = getHardWaypointsInfoFromRoutes(newRequestedRoute ? [newRequestedRoute] : null);
    this.hardWaypoints = this.isNewRouteEtaAfterCurrent ? newRouteWaypoints : currentRouteWaypoints; //so the chart time frame is adjusted to bigger route

    this.currentPlanDataPoints = VesselPerformanceModel!.createDataSet(currentApprovedRoute.route_geometry)
    this.requestedPlanDataPoints = newRequestedRoute?.route_geometry ? VesselPerformanceModel!.createDataSet(newRequestedRoute.route_geometry) : [];

    this.updatePerformanceData();
  }

  private async updatePerformanceData() {
    try {
      const sogs = this._sogData.filter(d => d.name !== "simulated");
      const powers = this._powerData.filter(d => d.name !== "simulated");
      const stws = this._stwData.filter(d => d.name !== "simulated");
      const currentImpacts = this._currentImpactData.filter(d => d.name !== "simulated");
      const rollAngles = this._rollAngleData.filter(d => d.name !== "simulated");

      const sogCurrent = VesselPerformanceModel!.extractOneParam('', ParameterType.SpeedOverGround, true, true, this.currentPlanDataPoints);
      const sogNew = VesselPerformanceModel!.extractOneParam('', ParameterType.SpeedOverGround, true, true, this.requestedPlanDataPoints)

      if (sogCurrent.length > 0)
        sogs.push({
          name: 'Current Plan',
          color: '--current-route',
          timeseries: PerformanceUtils.paramValuesToTimeseries(sogCurrent)
        })

      if (sogNew.length > 0)
        sogs.push({
          name: 'New Plan',
          color: '--requested-route',
          timeseries: PerformanceUtils.paramValuesToTimeseries(sogNew)
        })

      const powerCurrent = VesselPerformanceModel!.extractOneParam('', ParameterType.PropulsionPower, true, true, this.currentPlanDataPoints);
      const powerNew = VesselPerformanceModel!.extractOneParam('', ParameterType.PropulsionPower, true, true, this.requestedPlanDataPoints)

      if (powerCurrent.length > 0)
        powers.push({
          name: 'Current Plan',
          color: '--current-route',
          timeseries: PerformanceUtils.paramValuesToTimeseries(powerCurrent)
        })

      if (powerNew.length > 0)
        powers.push({
          name: 'New Plan',
          color: '--requested-route',
          timeseries: PerformanceUtils.paramValuesToTimeseries(powerNew)
        })

      const stwCurrent = VesselPerformanceModel!.extractOneParam('', ParameterType.SpeedThroughWater, true, true, this.currentPlanDataPoints);
      const stwNew = VesselPerformanceModel!.extractOneParam('', ParameterType.SpeedThroughWater, true, true, this.requestedPlanDataPoints)

      if (stwCurrent.length > 0)
        stws.push({
          name: 'Current Plan',
          color: '--current-route',
          timeseries: PerformanceUtils.paramValuesToTimeseries(stwCurrent)
        })

      if (stwNew.length > 0)
        stws.push({
          name: 'New Plan',
          color: '--requested-route',
          timeseries: PerformanceUtils.paramValuesToTimeseries(stwNew)
        })

      const currentImpactCurrent = VesselPerformanceModel!.extractOneParam('', ParameterType.CurrentImpact, false, true, this.currentPlanDataPoints);
      const currentImpactNew = VesselPerformanceModel!.extractOneParam('', ParameterType.CurrentImpact, false, true, this.requestedPlanDataPoints)

      if (currentImpactCurrent.length > 0)
        currentImpacts.push({
          name: 'Current Plan',
          color: '--current-route',
          timeseries: PerformanceUtils.paramValuesToTimeseries(currentImpactCurrent)
        })

      if (currentImpactNew.length > 0)
        currentImpacts.push({
          name: 'New Plan',
          color: '--requested-route',
          timeseries: PerformanceUtils.paramValuesToTimeseries(currentImpactNew)
        })

      const rollAngleCurrent = VesselPerformanceModel!.extractOneParam('', ParameterType.RollAngle, false, true, this.currentPlanDataPoints);
      const rollAngleNew = VesselPerformanceModel!.extractOneParam('', ParameterType.RollAngle, false, true, this.requestedPlanDataPoints)

      if (rollAngleCurrent.length > 0)
        if (this._rollAngleThresholdLimit !== null)
          {
              rollAngles.push({
                  name: "threshold limit",
                  color: "--performance-viewer-threshold-color",
                  timeseries: PerformanceUtils.addConstantToTimeseries(this.allTimestamps, this._rollAngleThresholdLimit)
              });
          }

        rollAngles.push({
          name: 'Current Plan',
          color: '--current-route',
          timeseries: PerformanceUtils.paramValuesToTimeseries(rollAngleCurrent)
        })

      if (rollAngleNew.length > 0)
        rollAngles.push({
          name: 'New Plan',
          color: '--requested-route',
          timeseries: PerformanceUtils.paramValuesToTimeseries(rollAngleNew)
        })

        


      this._chartsData.sog = sogs;
      this._chartsData.power = powers;
      this._chartsData.stw = stws;
      this._chartsData.currentImpact = currentImpacts;
      this._chartsData.rollAngle = rollAngles;
      this._debouncedExtractAllTimestamps();
    } catch (err) {
      console.warn(err);
    }
  }

  private async updateActualPerformanceData() {
    try {
      const sogs = this._sogData.filter(d => d.name !== "actual");
      const powers = this._powerData.filter(d => d.name !== "actual");
      const stws = this._stwData.filter(d => d.name !== "actual");

      const sog = VesselPerformanceModel!.extractOnePAPIParam(ParameterType.SpeedOverGround, true);
      if (sog.length > 0) {
        sogs.push({
          name: "actual",
          fillArea: true,
          color: "--performance-viewer-actual-color",
          timeseries: PerformanceUtils.paramValuesToTimeseries(sog)
        });

      }

      const power = VesselPerformanceModel!.extractOnePAPIParam(ParameterType.PropulsionPower, true);
      if (power.length > 0) {
        const timeseries: TimeseriesValues = PerformanceUtils.paramValuesToTimeseries(power);
        // round values to whole numbers
        timeseries.values = timeseries.values.map(number => number === null ? null : Math.round(number));

        powers.push({
          name: "actual",
          fillArea: true,
          color: "--performance-viewer-actual-color",
          timeseries: timeseries
        });
      }
      const stw = VesselPerformanceModel!.extractOnePAPIParam(ParameterType.SpeedThroughWater, true);
      
      if (stw.length > 0) {
        stws.push({
          name: "actual",
          fillArea: true,
          color: "--performance-viewer-actual-color",
          timeseries: PerformanceUtils.paramValuesToTimeseries(stw)
        });

      }

      this._sogData = sogs;
      this._powerData = powers;
      this._stwData = stws;

      this._debouncedExtractAllTimestamps();
    } catch (err) {
      console.warn(err);
    }
  }

  private extractAllTimestamps() {
    this.allTimestamps = this.isNewRouteEtaAfterCurrent ? extractAllTimestamps(this.newPilotPoints, this._chartsData, this.hardWaypoints) : extractAllTimestamps(this.pilotPoints, this._chartsData, this.hardWaypoints);
  }

  render() {
    return html`
    <div class="chart">
        <div>
          <div class="heading">
            <div class="title align-text">Speed over Ground (current vs new)</div>
            ${this.renderLengend()}
          </div>
          <voy-timeseries-chart
              id='sogchart'
              unit=${echartUtils.getUnit('sog')}
              .data=${this._chartsData[ChartNames.Sog]}
              .allTimestamps=${this.allTimestamps}
              .pilotPoints=${this.pilotPoints}
              .newPilotPoints=${this.newPilotPoints}
              .routesAndWaypoints=${this.hardWaypoints}
              .showLegend=${false}
              .showWaypoints=${true}
              .yAxisChartParams=${{
              nameRotate: 0,
              nameLocation: 'middle',
              padding: [0, 30, 0, 0],
              nameGap: 10
            }}
          >
          </voy-timeseries-chart>
      </div>
      <div>
          <span class="title">Propulsion - ME Power (current vs new)</span>
          <voy-timeseries-chart
              id='powerchart'
              unit=${echartUtils.getUnit('power')}
              .data=${this._chartsData[ChartNames.Power]}
              .allTimestamps=${this.allTimestamps}
              .pilotPoints=${this.pilotPoints}
              .newPilotPoints=${this.newPilotPoints}
              .routesAndWaypoints=${this.hardWaypoints}
              .showLegend=${false}
              .yAxisChartParams=${{
              nameRotate: 0,
              nameLocation: 'middle',
              padding: [0, 30, 0, 0],
              nameGap: 10
            }}
          >
          </voy-timeseries-chart> 
      </div>
                                 
    </div>`;
  }

  private renderLengend(){
    return html`
        <div class="custom-legend">
            <div class="legend-lines">
              <div class="legend-item">
                <div class="line actual"></div><span>Actual</span>
              </div>
              <div class="legend-item">
                <div class="line current"></div><span>Current plan</span>
              </div>
              <div class="legend-item">
                <div class="line new"></div><span>New plan</span>
              </div>
            </div>
            <div class="legend-icons">
                <div class="legend-item">
                  <div class="icon new-pilot-point">${SvgIcons.pilotIcon}</div><span>New pilot point</span>
                </div>
                <div class="legend-item">
                  <div class="icon current-pilot-point">${SvgIcons.pilotIcon}</div><span>Current pilot point</span>
                </div>
            </div>
        </div>
    `
  }
}